import {
  assessmentUnitKey,
  isLeafSelectedCapability,
  isLeafUnit,
  parseAssessmentUnitKey,
  sameAssessmentUnit,
} from './assessmentUnitUtils';

/** Sub-capability id sent to chat APIs (0 = capability owns questions directly). */
export function chatApiSubCapabilityId(
  subCapabilityId: number | string | null | undefined
): number {
  return isLeafUnit(subCapabilityId) ? 0 : Number(subCapabilityId) || 0;
}

/** Stable key for message/file state maps and suggestion buckets. */
export function chatMessageStorageKey(
  capabilityId: number | string | null | undefined,
  subCapabilityId: number | string | null | undefined
): string {
  return assessmentUnitKey(capabilityId, chatApiSubCapabilityId(subCapabilityId));
}

const MAX_STORED_UNUSED_SUGGESTIONS = 4;

/** Map API/DB unused keys (numeric sub-cap id) to UI thread keys (`cap-*` / `sub-*`). */
export function remapUnusedSuggestionsFromApi(
  unusedBySubCapability: Record<string, string[]> | null | undefined,
  selectedCapabilities?: Array<{
    capabilityId?: number;
    selectedSubCapabilities?: Array<{ subCapabilityId?: number | null }>;
  }> | null
): Record<string, string[]> {
  const caps = Array.isArray(selectedCapabilities) ? selectedCapabilities : [];
  const next: Record<string, string[]> = {};

  const findCapabilityIdForApiSub = (apiSubId: number): number | null => {
    for (const cap of caps) {
      const capId = Number(cap.capabilityId) || 0;
      if (!capId) {
        continue;
      }
      if (apiSubId === 0) {
        if (isLeafSelectedCapability(cap)) {
          return capId;
        }
        continue;
      }
      const units = cap.selectedSubCapabilities ?? [];
      if (units.some((unit) => Number(unit.subCapabilityId) === apiSubId)) {
        return capId;
      }
    }
    return null;
  };

  const assign = (storageKey: string, questions: string[]) => {
    const trimmed = questions
      .map((q) => String(q || '').trim())
      .filter(Boolean)
      .slice(0, MAX_STORED_UNUSED_SUGGESTIONS);
    if (trimmed.length) {
      next[storageKey] = trimmed;
    }
  };

  Object.entries(unusedBySubCapability ?? {}).forEach(([key, questions]) => {
    if (!Array.isArray(questions) || questions.length === 0) {
      return;
    }

    const parsed = parseAssessmentUnitKey(key);
    if (parsed) {
      const capId =
        parsed.capabilityId ?? findCapabilityIdForApiSub(parsed.subCapabilityId);
      if (capId != null) {
        assign(assessmentUnitKey(capId, parsed.subCapabilityId), questions);
      }
      return;
    }

    const apiSubId = Number(key);
    if (!Number.isFinite(apiSubId)) {
      return;
    }

    const capId = findCapabilityIdForApiSub(apiSubId);
    if (capId != null) {
      assign(assessmentUnitKey(capId, apiSubId), questions);
    } else if (apiSubId > 0) {
      assign(`sub-${apiSubId}`, questions);
    }
  });

  return next;
}

export function chatThreadsMatch(
  leftCapabilityId: number | string | null | undefined,
  leftSubCapabilityId: number | string | null | undefined,
  rightCapabilityId: number | string | null | undefined,
  rightSubCapabilityId: number | string | null | undefined
): boolean {
  return sameAssessmentUnit(
    leftCapabilityId,
    leftSubCapabilityId,
    rightCapabilityId,
    rightSubCapabilityId
  );
}
